/**
 * @author: lv.chen
 * @date: 2025/7/11
 * @desc: Market Profile页面指标宽表
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { DBTable } from '@home/components/TeaComponents';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import { MetricTips1 } from '@intelligence/v3/components/TableComponents/MetricTips';
import { CommonColumnBuilders } from '@intelligence/v3/utils/columns';
import { generateMetricRatioKey } from '@intelligence/v3/utils/metric';
import { getTopCountries, getMarketProfileDetailsRoutePath, navigateToMarketProfileDetailsByPath } from '../../utils';
import { DATASOURCE_KEY_MAP, NEWZOO_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';
import {
  NEWZOO_TOTAL_REVENUE_METRIC_KEY,
  NEWZOO_TOTAL_PLAYERS_METRIC_KEY,
} from '@intelligence/v3/_config/metrics/newzoo';

import type { TableColumn, PagingQuery } from 'tea-component';

type IMetricNameSpecialGetter = (metric: IPrimaryKeyMetric, metricName: string) => string;

/**
 * Market Profile页面指标宽表组件props类型
 */
interface IMarketMetricsTableProps extends IBaseProps {
  /**
   * 是否加载数据中
   */
  loading?: boolean;
  /**
   * 是否无数据
   */
  noData?: boolean;
  /**
   * 指标列表
   */
  metrics: IPrimaryKeyMetric[] | null;
  /**
   * Market Profile页面列表数据
   */
  data: IPaginationData<IGenreMarketProfile>;
  /**
   * 国家映射
   */
  countryMap: IRecord<IRegionCountryEntity>;
  /**
   * 当前勾选的value
   */
  checkedList: string[];
  /**
   * 当前勾选的value的change回调
   */
  onCheckedListChange: (checkedList: string[]) => void;
  /**
   * 排序状态列表
   */
  sorts: IRatioMetricSortState<IGenreMarketProfile>[];
  /**
   * 排序的回调
   * @param sorts 排序状态列表
   */
  onSortsChange?: (sorts: IRatioMetricSortState<IGenreMarketProfile>[]) => void;
  /**
   * 分页
   */
  pagingQuery: PagingQuery;
  /**
   * 分页的回调
   */
  onPagingChange?: (pagingQuery: PagingQuery) => void;
  getMetricNameSpecial?: IMetricNameSpecialGetter;
  updateDeps?: React.DependencyList;
}

const getRecordKey = (marketProfile?: IGenreMarketProfile | null): string => marketProfile?.market || '';

const MarketMetricsTable: React.FC<IMarketMetricsTableProps> = (props) => {
  const {
    style,
    className,
    loading = true,
    noData = true,
    entityType,
    metrics,
    data,
    countryMap,
    checkedList,
    onCheckedListChange,
    sorts,
    onSortsChange,
    pagingQuery,
    onPagingChange,
    getMetricNameSpecial,
    updateDeps,
  } = props;

  const { locale, formatMessage } = useIntl();

  /**
   * 创建指标环比取值getters
   * @param key 指标key（包含数据源、指标）
   */
  const createMetricRatioValueGetter = (key: string) => {
    return (cvm: IGenreMarketProfile) => {
      const metricRatioKey = generateMetricRatioKey(key);
      return cvm?.[metricRatioKey] ?? null;
    };
  };

  const handleNavigateToMarketProfileDetailsByPath = (cvm: IGenreMarketProfile) => {
    const countryCode = cvm?.market;
    navigateToMarketProfileDetailsByPath({
      pageId: 'MarketProfilPC',
      countryCode,
      countryName: countryMap?.[countryCode]?.country_name_en,
    });
  };

  /**
   * 表格columns配置
   */
  const columns: TableColumn<IGenreMarketProfile>[] = useMemo(() => {
    const metricColumns = (() => {
      if (!Array.isArray(metrics) || metrics.length === 0) return [];
      return metrics.map((item) => {
        const key = item?.key;
        const dataSource = item?.dataSource;
        const metric = item?.metric;
        const metricTips = (
          <MetricTips1
            // @ts-ignore
            locale={locale}
            entityType={entityType}
            dataSource={dataSource}
            metric={metric}
            description={formatMessage({ id: `dbi_market_profile_metric_tips_${key}` })}
          />
        );
        return CommonColumnBuilders.buildKMBMetricColumn<IGenreMarketProfile>({
          locale,
          key,
          align: 'center',
          width: 260,
          dataSource,
          metric,
          metricTips,
          getValue: (cvm) => cvm?.[key] || null,
          getRatioValue: createMetricRatioValueGetter(key),
          sorts,
          onSortsChange,
        });
      });
    })();

    return [
      /**
       * checkbox勾选column
       */
      CommonColumnBuilders.buildCheckerColumn({
        fixed: 'left',
        getRecordCheckOptionValue: (cvm) => cvm?.market,
        values: getTopCountries(data?.list),
        value: checkedList,
        onChange: onCheckedListChange,
      }),
      /**
       * 序号column
       */
      CommonColumnBuilders.buildSequenceColumn({
        fixed: 'left',
        width: 50,
        getSequence: (index, cvm) => {
          return cvm?.rank ?? Number.NaN;
        },
      }),
      /**
       * Market column
       */
      CommonColumnBuilders.buildCountryColumn<IGenreMarketProfile>({
        fixed: 'left',
        locale,
        countryMap,
        getLinkUrl: (cvm) => {
          return getMarketProfileDetailsRoutePath({
            pageId: 'MarketProfilPC',
            countryCode: cvm?.market,
            countryName: countryMap?.[cvm?.market]?.country_name_en,
          });
        },
        onClick: (countryCode, cvm) => {
          handleNavigateToMarketProfileDetailsByPath(cvm);
        },
      }),
      ...metricColumns,
      CommonColumnBuilders.buildKMBMetricColumn<IGenreMarketProfile>({
        locale,
        key: NEWZOO_TOTAL_REVENUE_METRIC_KEY,
        align: 'center',
        width: 360,
        showRatio: false,
        dataSource: DATASOURCE_KEY_MAP.NEWZOO,
        metric: NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE,
        metricName: getMetricNameSpecial?.(
          {
            key: NEWZOO_TOTAL_REVENUE_METRIC_KEY,
            dataSource: DATASOURCE_KEY_MAP.NEWZOO,
            metric: NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE,
          },
          '',
        ),
        getValue: (cvm) => cvm?.[NEWZOO_TOTAL_REVENUE_METRIC_KEY] ?? null,
        sorts,
        onSortsChange,
        metricTips: (
          <MetricTips1
            // @ts-ignore
            locale={locale}
            entityType={entityType}
            dataSource={DATASOURCE_KEY_MAP.NEWZOO}
            metric={NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE}
            description={formatMessage({
              id: `dbi_market_profile_metric_tips_${NEWZOO_TOTAL_REVENUE_METRIC_KEY}`,
            })}
          />
        ),
      }),
      CommonColumnBuilders.buildKMBMetricColumn<IGenreMarketProfile>({
        locale,
        key: NEWZOO_TOTAL_PLAYERS_METRIC_KEY,
        align: 'center',
        width: 360,
        showRatio: false,
        dataSource: DATASOURCE_KEY_MAP.NEWZOO,
        metric: NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS,
        metricName: getMetricNameSpecial?.(
          {
            key: NEWZOO_TOTAL_PLAYERS_METRIC_KEY,
            dataSource: DATASOURCE_KEY_MAP.NEWZOO,
            metric: NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS,
          },
          '',
        ),
        getValue: (cvm) => cvm?.[NEWZOO_TOTAL_PLAYERS_METRIC_KEY] ?? null,
        sorts,
        onSortsChange,
        metricTips: (
          <MetricTips1
            // @ts-ignore
            locale={locale}
            entityType={entityType}
            dataSource={DATASOURCE_KEY_MAP.NEWZOO}
            metric={NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS}
            description={formatMessage({
              id: `dbi_market_profile_metric_tips_${NEWZOO_TOTAL_PLAYERS_METRIC_KEY}`,
            })}
          />
        ),
      }),
      CommonColumnBuilders.buildActionsColumn<IGenreMarketProfile>({
        align: 'center',
        onClick: (key: string, cvm) => {
          handleNavigateToMarketProfileDetailsByPath(cvm);
        },
      }),
    ];
  }, [
    locale,
    entityType,
    countryMap,
    metrics,
    checkedList,
    sorts,
    data,
    onCheckedListChange,
    onSortsChange,
    getMetricNameSpecial,
    updateDeps,
  ]);

  return (
    <CardSlotV2 style={style} className={classnames('dbi-market-profile-metrics-table', className)}>
      {loading && (
        <div className="dbi-market-profile-global-performance__loading-wrapper" style={{ fontSize: 14, height: 410 }}>
          <Loading content={formatMessage({ id: 'common_loading' })} />
        </div>
      )}
      {!loading && noData && (
        <div style={{ fontSize: 14, height: 410 }}>
          <NoData content={formatMessage({ id: 'common_nodata' })} />
        </div>
      )}
      {!loading && !noData && (
        <DBTable
          className="db-table--has-check-sequence"
          bordered="all"
          loading={loading}
          recordKey={getRecordKey}
          pagingQuery={pagingQuery}
          onPagingChange={onPagingChange}
          columns={columns}
          records={data?.list || []}
        />
      )}
    </CardSlotV2>
  );
};

export default MarketMetricsTable;
